import type { PatchContactPayload } from "../Interfaces/ContactInterface";
import type {
  Contact360Contact,
  Patient360Patient,
} from "../Interfaces/Patient360Interface";
import type { PatchPatientCrmPayload, Patient } from "../Apis/patients";
import type { LeadDetails, Opportunity } from "../types/leadDetails";
import {
  isMetaLeadSource,
  resolveLeadSourceForApi,
  ROUTING_GROUP_LEAD_SOURCE_FALLBACK,
} from "../constants/leadSourceOptions";
import { LEAD_DETAILS_PAYOR_TYPE_OPTIONS } from "./multiSelectFilterUtils";

/** Form field shape shared by Add New Contact and Edit Contact flows */
export interface ContactFormValues {
  patientName: string;
  gender: string;
  phoneNumber: string;
  email: string;
  displayNumber: string;
  whatsappNumber: string;
  dob: string;
  age: string;
  searchAddress: string;
  city: string;
  state: string;
  country: string;
  pincode: string;
  unit: string;
  source: string;
  channel: string;
  preferredUnit: string;
  payerType: string;
  opportunity: string;
}

export const EMPTY_CONTACT_FORM_VALUES: ContactFormValues = {
  patientName: "",
  gender: "",
  phoneNumber: "",
  email: "",
  displayNumber: "",
  whatsappNumber: "",
  dob: "",
  age: "",
  searchAddress: "",
  city: "",
  state: "",
  country: "",
  pincode: "",
  unit: "",
  source: "",
  channel: "",
  preferredUnit: "",
  payerType: "",
  opportunity: "",
};

function formatDobForForm(dateOfBirth: string | null | undefined): string {
  if (!dateOfBirth?.trim()) return "";
  const d = new Date(dateOfBirth.trim());
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function getAgeFromDobString(dob: string): string {
  const trimmed = (dob ?? "").trim();
  if (!trimmed) return "";
  const d = new Date(trimmed);
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date();
  if (d > today) return "";
  let age = today.getFullYear() - d.getFullYear();
  const monthDiff = today.getMonth() - d.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < d.getDate())) {
    age -= 1;
  }
  return age >= 0 ? String(age) : "";
}

/** Display format for date pickers (e.g. "26 September 1969"). */
export function formatDobForDisplay(
  dateOfBirth: string | null | undefined
): string {
  if (!dateOfBirth?.trim()) return "";
  const d = new Date(dateOfBirth.trim());
  if (Number.isNaN(d.getTime())) return "";
  // DOB is a calendar date (parsed as UTC midnight for date-only input) —
  // render in UTC so the displayed day never shifts with the viewer timezone.
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** Calculate age in years from a DOB string (ISO or display format). */
export function getAgeFromDob(dob: string): string {
  return getAgeFromDobString(dob);
}

/** Derive approximate DOB (Jan 1) from age; returns ISO YYYY-MM-DD. */
export function getDobFromAge(age: string): string {
  const trimmed = (age ?? "").trim();
  if (!trimmed) return "";
  const ageNum = parseInt(trimmed, 10);
  if (Number.isNaN(ageNum) || ageNum < 0) return "";
  const year = new Date().getFullYear() - ageNum;
  return `${year}-01-01`;
}

/** Derive approximate DOB from age in calendar display format. */
export function getDobDisplayFromAge(age: string): string {
  const iso = getDobFromAge(age);
  return iso ? formatDobForDisplay(iso) : "";
}

export function normalizeGenderToFormValue(
  gender: string | null | undefined
): string {
  if (!gender) return "";
  const lower = gender.trim().toLowerCase();
  if (lower === "m" || lower === "male") return "male";
  if (lower === "f" || lower === "female") return "female";
  if (lower === "o" || lower === "other") return "other";
  return "";
}

function leadGenderToFormValue(gender: string | undefined): string {
  if (gender === "M") return "male";
  if (gender === "F") return "female";
  if (gender === "O") return "other";
  return normalizeGenderToFormValue(gender);
}

function toPhoneDigits(s: string): string {
  return (s ?? "").replace(/\D/g, "");
}

const KNOWN_PAYER_TYPE_VALUES = [
  "cash",
  "insurance",
  "corporate",
  "panel",
] as const;

/** Booking flows (Book Appointment / Book Diagnostic) — same API keys as opportunity payorType filter. */
export const PAYER_TYPE_OPTIONS = LEAD_DETAILS_PAYOR_TYPE_OPTIONS;

export const PAYER_TYPE_FORM_VALUES = PAYER_TYPE_OPTIONS.map(
  (option) => option.value
);

type PayorTypeCanonical = (typeof PAYER_TYPE_FORM_VALUES)[number];

function normalizePayorTypeToCanonicalKey(
  payorType: string | null | undefined
): PayorTypeCanonical | "" {
  if (payorType == null) return "";
  const trimmed = String(payorType).trim();
  if (!trimmed) return "";

  if (PAYER_TYPE_FORM_VALUES.includes(trimmed as PayorTypeCanonical)) {
    return trimmed as PayorTypeCanonical;
  }

  const key = trimmed.toLowerCase().replace(/\s+/g, "-");

  if (key === "insurance" || key === "tpa") return "Insurance";
  if (
    key === "corporate" ||
    key === "corporate-credit" ||
    key === "corporate-cash" ||
    key === "staff" ||
    key === "public-sector-undertaking" ||
    key === "panel"
  ) {
    return "Corporate";
  }
  if (
    key === "self-pay" ||
    key === "selfpay" ||
    key === "cash" ||
    key === "other-cash"
  ) {
    return "Self-Pay";
  }

  return "";
}

/** Map API payerType to form select value; blank when missing or empty. */
export function normalizePayerTypeToFormValue(
  payerType: string | null | undefined
): string {
  if (payerType == null) return "";
  const trimmed = String(payerType).trim();
  if (!trimmed) return "";
  const lower = trimmed.toLowerCase();
  const match = KNOWN_PAYER_TYPE_VALUES.find((v) => v === lower);
  return match ?? lower;
}

/** HIS payorType or CRM payerType from an opportunity record. */
export function getOpportunityPayorTypeRaw(
  opp:
    | (Pick<Opportunity, "payorType"> & { payerType?: string | null })
    | null
    | undefined
): string | null | undefined {
  if (!opp) return undefined;
  return opp.payorType ?? opp.payerType ?? undefined;
}

/** Merge booking-list opportunity with lead-details list so payorType is available when only one API returns it. */
export function resolveOpportunityForPayorSync(
  opportunityId: string,
  primary: Opportunity[],
  fallback?: Opportunity[]
): Opportunity | undefined {
  const primaryOpp = primary.find((o) => o.id === opportunityId);
  const fallbackOpp = fallback?.find((o) => o.id === opportunityId);
  if (!primaryOpp && !fallbackOpp) return undefined;
  if (!primaryOpp) return fallbackOpp;
  if (!fallbackOpp) return primaryOpp;

  const payorType =
    getOpportunityPayorTypeRaw(primaryOpp) ??
    getOpportunityPayorTypeRaw(fallbackOpp);
  const payorName =
    primaryOpp.payorName?.trim() || fallbackOpp.payorName?.trim();

  return {
    ...primaryOpp,
    ...(payorName ? { payorName } : {}),
    ...(payorType ? { payorType } : {}),
  };
}

/** Map payorType from opportunity id using primary + fallback opportunity lists. */
export function resolvePayerTypeFormValueForOpportunity(
  opportunityId: string,
  primary: Opportunity[],
  fallback?: Opportunity[]
): string {
  const opp = resolveOpportunityForPayorSync(opportunityId, primary, fallback);
  return normalizeOpportunityPayorTypeToFormValue(
    getOpportunityPayorTypeRaw(opp)
  );
}

/** Map opportunity payorType (HIS raw or CRM) to booking form dropdown value. */
export function normalizeOpportunityPayorTypeToFormValue(
  payorType: string | null | undefined
): string {
  const canonical = normalizePayorTypeToCanonicalKey(payorType);
  if (canonical) return canonical;

  const legacyForm = normalizePayerTypeToFormValue(payorType);
  const legacyMap: Record<string, PayorTypeCanonical> = {
    insurance: "Insurance",
    corporate: "Corporate",
    cash: "Self-Pay",
    panel: "Corporate",
  };
  return legacyMap[legacyForm] ?? "";
}

/** Map booking form payer type to PATCH payorType payload. */
export function formPayerTypeToPayorType(formValue: string): string {
  const trimmed = (formValue ?? "").trim();
  if (!trimmed) return "";
  return normalizePayorTypeToCanonicalKey(trimmed) || trimmed;
}

/** True when user changed payer type from the value auto-filled at opportunity selection. */
export function shouldPatchOpportunityPayorType(
  selectedPayerType: string,
  opportunityPayerTypeAtSelection: string
): boolean {
  return selectedPayerType !== opportunityPayerTypeAtSelection;
}

type ContactLikeRecord = Pick<
  Contact360Contact,
  | "fullName"
  | "gender"
  | "primaryPhone"
  | "email"
  | "whatsappNumber"
  | "dateOfBirth"
  | "age"
  | "addressLine1"
  | "city"
  | "state"
  | "pincode"
  | "primaryFacilityId"
  | "utmSource"
  | "leadSource"
  | "utmCampaign"
  | "preferredUnit"
  | "payerType"
> & {
  /** CRM-only alternate number (CAR-2844); absent/null means same as phone */
  displayNumber?: string | null;
};

/** True when stored value is empty or equals the registered phone (last-10 digit match). */
export function isPhoneSameAsRegistered(
  value: string | null | undefined,
  registeredPhone: string | null | undefined
): boolean {
  const valueDigits = toPhoneDigits(value ?? "");
  if (!valueDigits) return true;
  const phoneDigits = toPhoneDigits(registeredPhone ?? "");
  if (!phoneDigits) return false;
  if (valueDigits === phoneDigits) return true;
  return valueDigits.slice(-10) === phoneDigits.slice(-10);
}

/** Map contact or patient API record to form values for pre-fill */
export function contactRecordToFormValues(
  record: ContactLikeRecord
): ContactFormValues {
  const phone = (record.primaryPhone ?? "").trim();
  const whatsapp = (record.whatsappNumber ?? "").trim();
  // Prefill stored display number; fall back to phone only for the form display
  // when CRM has none (checkbox will show "Same as phone").
  const storedDisplay = (record.displayNumber ?? "").trim();
  const display = storedDisplay || phone;
  const dob = formatDobForForm(record.dateOfBirth ?? null);
  const ageFromApi =
    record.age != null && typeof record.age === "number"
      ? String(record.age)
      : "";
  const age = ageFromApi || getAgeFromDobString(dob);

  return {
    ...EMPTY_CONTACT_FORM_VALUES,
    patientName: (record.fullName ?? "").trim(),
    gender: normalizeGenderToFormValue(record.gender ?? undefined),
    phoneNumber: phone,
    email: (record.email ?? "").trim(),
    displayNumber: display,
    whatsappNumber: whatsapp || phone,
    dob,
    age,
    searchAddress: (record.addressLine1 ?? "").trim(),
    city: (record.city ?? "").trim(),
    state: (record.state ?? "").trim(),
    country: "India",
    pincode: (record.pincode ?? "").trim(),
    unit: (record.primaryFacilityId ?? "").trim(),
    source: (record.utmSource ?? record.leadSource ?? "").trim(),
    channel: (
      (record as { sourceChannel?: string | null }).sourceChannel ?? ""
    ).trim(),
    preferredUnit: (record.preferredUnit ?? "").trim(),
    payerType: normalizePayerTypeToFormValue(record.payerType),
  };
}

/** Prefer payerType from patient 360 when present; otherwise use GET patient value. */
export function mergePatientPayerTypeForForm(
  patient: Patient,
  patient360?: Patient360Patient | null
): Patient {
  const from360 = patient360?.payerType;
  if (from360 != null && String(from360).trim() !== "") {
    return { ...patient, payerType: String(from360).trim() };
  }
  return patient;
}

export function patientToFormValues(patient: Patient): ContactFormValues {
  return contactRecordToFormValues(patient);
}

export function contact360ToFormValues(
  contact: Contact360Contact
): ContactFormValues {
  return contactRecordToFormValues(contact);
}

/** Fallback when only sidebar LeadDetails is available (partial data) */
export function leadDetailsToFormValues(
  leadData: LeadDetails
): ContactFormValues {
  const phone = (leadData.phone ?? "").trim();
  const dob = formatDobForForm(leadData.dateOfBirth ?? null);
  const ageFromLead = leadData.age != null ? String(leadData.age) : "";
  const age = ageFromLead || getAgeFromDobString(dob);
  return withEditContactSourceFallbacks(
    {
      ...EMPTY_CONTACT_FORM_VALUES,
      patientName: (leadData.patientName ?? "").trim(),
      gender: leadGenderToFormValue(leadData.gender),
      phoneNumber: phone,
      displayNumber: phone,
      dob,
      age,
      source: (leadData.contactSource?.source ?? "").trim(),
      channel: (leadData.contactSource?.channel ?? "").trim(),
    },
    leadData
  );
}

function opportunityLeadSource(opp: Opportunity): string {
  return (opp.leadSource ?? opp.utmSource ?? "").trim();
}

function leadSourcesMatch(a: string, b: string): boolean {
  if (!a || !b) return false;
  if (a.toLowerCase() === b.toLowerCase()) return true;
  const ca = resolveLeadSourceForApi(a, ROUTING_GROUP_LEAD_SOURCE_FALLBACK);
  const cb = resolveLeadSourceForApi(b, ROUTING_GROUP_LEAD_SOURCE_FALLBACK);
  return ca.toLowerCase() === cb.toLowerCase();
}

/**
 * CAR-2845: Edit Contact Source/Channel (and Unit when needed for Save) often
 * live on the opportunity, not the contact identity. Fill blanks from a single
 * matching opportunity / sidebar contactSource, then canonicalize Source so the
 * dropdown option matches (e.g. Voice OC → VOICE_OC).
 *
 * Source/Channel/Unit always come from the same opportunity — never stitch
 * across rows. When Source is already set (e.g. HIS), Channel/Unit only fill
 * from an opportunity with the same Source.
 */
export function withEditContactSourceFallbacks(
  values: ContactFormValues,
  leadData?: Pick<LeadDetails, "contactSource" | "opportunities"> | null
): ContactFormValues {
  let source = (values.source ?? "").trim();
  let channel = (values.channel ?? "").trim();
  let unit = (values.unit ?? "").trim();

  const opportunities: Opportunity[] = leadData?.opportunities ?? [];

  if (!source) {
    const donor = opportunities.find((opp) => opportunityLeadSource(opp));
    if (donor) {
      source = opportunityLeadSource(donor);
      if (!channel) channel = (donor.sourceChannel ?? "").trim();
      if (!unit) unit = (donor.primaryFacilityId ?? "").trim();
    }
  } else {
    const matching = opportunities.find((opp) =>
      leadSourcesMatch(source, opportunityLeadSource(opp))
    );
    if (matching) {
      if (!channel) channel = (matching.sourceChannel ?? "").trim();
      if (!unit) unit = (matching.primaryFacilityId ?? "").trim();
    }
  }

  if (!source) {
    source = (leadData?.contactSource?.source ?? "").trim();
  }
  if (!channel) {
    channel = (leadData?.contactSource?.channel ?? "").trim();
  }

  source = resolveLeadSourceForApi(source, ROUTING_GROUP_LEAD_SOURCE_FALLBACK);
  if (isMetaLeadSource(source)) {
    channel = "";
  }

  return {
    ...values,
    source,
    channel,
    unit,
  };
}

/** Map form gender to HIS upsert codes. Blank stays omitted (CAR-2816). */
export function formGenderToHisCode(
  gender: string | null | undefined
): "M" | "F" | "O" | undefined {
  const normalized = normalizeGenderToFormValue(gender);
  if (normalized === "male") return "M";
  if (normalized === "female") return "F";
  if (normalized === "other") return "O";
  return undefined;
}

/** Map form gender to PATCH enum. Empty must not become `"male"` (CAR-2816). */
export function formGenderToPatch(
  gender: string
): "male" | "female" | "other" | undefined {
  const normalized = normalizeGenderToFormValue(gender);
  if (
    normalized === "male" ||
    normalized === "female" ||
    normalized === "other"
  ) {
    return normalized;
  }
  return undefined;
}

/**
 * Address object for POST /consumer/upsert.
 * Omit when Search Address / City / State / Pincode are all blank.
 * Country alone does not count: "Enter address manually" auto-sets India,
 * which is not an agent-entered address (CAR-2816).
 */
export function buildOptionalUpsertAddress(values: {
  searchAddress?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  pincode?: string | null;
}):
  | {
      Address: string;
      pincode: string;
      City: string;
      State: string;
      Country?: string;
    }
  | undefined {
  const Address = (values.searchAddress ?? "").trim();
  const City = (values.city ?? "").trim();
  const State = (values.state ?? "").trim();
  const pincode = (values.pincode ?? "").trim();
  const Country = (values.country ?? "").trim();
  if (!Address && !City && !State && !pincode) {
    return undefined;
  }
  return {
    Address,
    pincode,
    City,
    State,
    ...(Country ? { Country } : {}),
  };
}

function dobToISO(dob: string): string | undefined {
  if (!dob?.trim()) return undefined;
  const d = new Date(dob.trim());
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

/** Build PATCH payload with only fields changed since open (edit contact) */
export function buildPatchPayloadFromFormDiff(
  initial: ContactFormValues,
  current: ContactFormValues
): PatchContactPayload {
  const payload: PatchContactPayload = {};

  const pName = current.patientName.trim();
  if (pName !== initial.patientName.trim())
    payload.fullName = pName || undefined;

  const phone = toPhoneDigits(current.phoneNumber);
  if (phone !== toPhoneDigits(initial.phoneNumber)) {
    payload.primaryPhone = phone || undefined;
  }

  const whatsapp = toPhoneDigits(current.whatsappNumber);
  if (whatsapp !== toPhoneDigits(initial.whatsappNumber)) {
    payload.whatsappNumber = whatsapp || undefined;
  }

  const e = current.email.trim();
  if (e !== initial.email.trim()) payload.email = e || undefined;

  const currentGender = formGenderToPatch(current.gender);
  const initialGender = formGenderToPatch(initial.gender);
  if (currentGender !== initialGender && currentGender !== undefined) {
    payload.gender = currentGender;
  }

  const dobIso = dobToISO(current.dob);
  const initDobIso = dobToISO(initial.dob);
  if (dobIso !== initDobIso) payload.dateOfBirth = dobIso;

  const addr = current.searchAddress.trim();
  if (addr !== initial.searchAddress.trim())
    payload.addressLine1 = addr || undefined;

  const c = current.city.trim();
  if (c !== initial.city.trim()) payload.city = c || undefined;

  const st = current.state.trim();
  if (st !== initial.state.trim()) payload.state = st || undefined;

  const pin = current.pincode.trim();
  if (pin !== initial.pincode.trim()) payload.pincode = pin || undefined;

  const src = current.source.trim();
  if (src !== initial.source.trim()) {
    payload.utmSource = src || undefined;
    payload.leadSource = src || undefined;
  }

  const ch = current.channel.trim();
  if (ch !== initial.channel.trim()) payload.sourceChannel = ch || undefined;

  const unit = current.preferredUnit.trim();
  if (unit !== initial.preferredUnit.trim())
    payload.preferredUnit = unit || undefined;

  const facility = current.unit.trim();
  if (facility !== initial.unit.trim())
    payload.primaryFacilityId = facility || undefined;

  return payload;
}

function phoneCrmApiEqual(a: string | null, b: string | null): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  const aDigits = toPhoneDigits(a);
  const bDigits = toPhoneDigits(b);
  if (aDigits === bDigits) return true;
  return aDigits.slice(-10) === bDigits.slice(-10);
}

/**
 * Build PATCH payload for patient CRM-only fields (CAR-2844).
 * When "Same as phone" is ticked, send null (blank stored value = same as phone).
 * Sends only changed fields among displayNumber, whatsappNumber, email, primaryFacilityId.
 */
export function buildPatientCrmPatchPayloadFromFormDiff(
  initial: ContactFormValues,
  current: ContactFormValues,
  options: {
    displaySameAsPhone: boolean;
    whatsappSameAsPhone: boolean;
  }
): PatchPatientCrmPayload {
  const payload: PatchPatientCrmPayload = {};

  const initialDisplay: string | null = isPhoneSameAsRegistered(
    initial.displayNumber,
    initial.phoneNumber
  )
    ? null
    : initial.displayNumber.trim() || null;
  const nextDisplay: string | null = options.displaySameAsPhone
    ? null
    : current.displayNumber.trim() || null;
  if (!phoneCrmApiEqual(initialDisplay, nextDisplay)) {
    payload.displayNumber = nextDisplay;
  }

  const initialWhatsapp: string | null = isPhoneSameAsRegistered(
    initial.whatsappNumber,
    initial.phoneNumber
  )
    ? null
    : initial.whatsappNumber.trim() || null;
  const nextWhatsapp: string | null = options.whatsappSameAsPhone
    ? null
    : current.whatsappNumber.trim() || null;
  if (!phoneCrmApiEqual(initialWhatsapp, nextWhatsapp)) {
    payload.whatsappNumber = nextWhatsapp;
  }

  const initialEmail = initial.email.trim() || null;
  const nextEmail = current.email.trim() || null;
  if (initialEmail !== nextEmail) {
    payload.email = nextEmail;
  }

  const initialFacility = initial.unit.trim() || null;
  const nextFacility = current.unit.trim() || null;
  if (initialFacility !== nextFacility) {
    payload.primaryFacilityId = nextFacility;
  }

  return payload;
}
